<script>
	import { push } from 'svelte-spa-router';
	import fetchData from '../../api.js';
	import UserStore from '../../stores/user';
	import { cartsStore } from '../../stores/cartStore';
	import { activeCart, activeCartId, setActiveCart, clearActiveCart } from '../../stores/activeCart';
	import { loadCatalogMeta, storeName } from '../../lib/catalog';
	import { money, imgUrl, bySize, cartTotals, formatDate, MAIN_STORE, VAT_PERCENT } from '../../lib/format';
	import AppShell from '../../components/AppShell.svelte';
	import CustomerPicker from '../../components/CustomerPicker.svelte';
	import Icon from '../../components/Icon.svelte';

	export let params = {};

	let cart = null;
	let loading = true;
	let error = '';
	let busy = false;
	let picking = false;
	let sentNumber = null;
	let stores = {};

	$: if (params && params.cart_id) load(params.cart_id);
	$: readOnly = !!(cart && cart.order_number);
	$: items = (cart && cart.items) || [];
	$: totals = cartTotals(items);
	$: groups = groupItems(items);
	$: models = new Set(items.map((i) => String(i.parent_id))).size;
	$: sections = brandSections(groups);

	// a mixed cart is shown in one section per brand, each priced on its own price list
	const BRAND_ORDER = ['ALDO MORO', 'ELIXIR', 'VIBE'];
	const PRICE_RULE = { VIBE: 'מחירון 3 · מחיר לצרכן ÷ 2.1', 'ALDO MORO': 'מחירון 2 · מחיר לצרכן ÷ 2.3', ELIXIR: 'מחירון 2 · מחיר לצרכן ÷ 2.3' };

	function brandSections(list) {
		const map = new Map();
		for (const g of list) {
			const b = g.brand || 'אחר';
			if (!map.has(b)) map.set(b, { brand: b, rule: PRICE_RULE[b] || '', groups: [], units: 0, total: 0 });
			const s = map.get(b);
			s.groups.push(g);
			s.units += g.units;
			s.total += g.total;
		}
		const rank = (b) => (BRAND_ORDER.indexOf(b) + 1) || 99;
		return [...map.values()].sort((a, b) => rank(a.brand) - rank(b.brand));
	}

	loadCatalogMeta().then((m) => (stores = m.stores));

	function imageFor(i) {
		const imgs = i.colorImages || [];
		const m = imgs.find((x) => x.color === i.color) || imgs[0];
		return m ? imgUrl(m.image_url) : '';
	}

	// one block per model + color, sizes inside
	function groupItems(list) {
		const map = new Map();
		for (const i of list) {
			const key = i.parent_id + '|' + (i.color || '');
			if (!map.has(key)) {
				map.set(key, {
					key, name: (i.name || '').trim(), model: i.parent_sku || i.sku, color: i.color || '', brand: (i.vendor || '').trim(),
					// the screens show the wholesale price only; the customer's discount is given at the warehouse
					image: imageFor(i), unitPrice: Number(i.price),
					rows: [], units: 0, total: 0
				});
			}
			const g = map.get(key);
			g.rows.push(i);
			g.units += Number(i.quantity) || 0;
			g.total += (Number(i.price) || 0) * (Number(i.quantity) || 0);
		}
		for (const g of map.values()) {
			g.rows.sort((a, b) => bySize(a.size, b.size));
			// sizes split by the store they come from, main warehouse first
			const stores = [...new Set(g.rows.map((i) => String(i.store)))]
				.sort((a, b) => (a === MAIN_STORE ? -1 : b === MAIN_STORE ? 1 : Number(a) - Number(b)));
			g.byStore = stores.map((s) => ({ store: s, rows: g.rows.filter((i) => String(i.store) === s) }));
		}
		return [...map.values()];
	}

	async function load(id, quiet = false) {
		if (!quiet) loading = true;
		error = '';
		const res = await fetchData({ action: 'agents_get_cart', token: $UserStore.data, cart_id: id });
		if (!res) {
			error = 'לא הצלחנו לטעון את הסל. בדוק את החיבור לאינטרנט.';
		}
		cart = res && res.data && res.data.id ? res.data : null;
		if (cart && !cart.order_number) {
			if (String($activeCartId) !== String(cart.id)) setActiveCart(cart.id);
			else activeCart.set(cart);
		}
		loading = false;
	}

	async function refreshCartsList() {
		const res = await fetchData({ action: 'agents_get_carts', token: $UserStore.data });
		cartsStore.set((res && res.data) || {});
	}

	function changeQty(item, delta) {
		return setQty(item, (Number(item.quantity) || 0) + delta);
	}

	// a quantity typed on the keyboard: whole number, not more than the stock (unless lowering a line whose stock dropped)
	function typedQty(item, input) {
		const current = Number(item.quantity) || 0;
		const typed = Math.floor(Number(input.value));
		if (input.value.trim() === '' || isNaN(typed) || typed === current) {
			input.value = current;
			return;
		}
		const max = Math.max(Number(item.stock) || 0, current);
		let n = Math.max(0, typed);
		if (n > max) {
			n = max;
			error = `${item.size}: ${max > 1 ? `במלאי יש רק ${max} יחידות` : max === 1 ? 'במלאי יש רק יחידה אחת' : 'המידה אזלה במלאי'}`;
		}
		input.value = n;
		if (n === 0 && !confirm(`להסיר את מידה ${item.size} מהסל?`)) {
			input.value = current;
			return;
		}
		if (n !== current) setQty(item, n, true);
	}

	async function setQty(item, n, keepError = false) {
		// show the new number right away, then save
		cart.items = cart.items.map((i) => (i.id === item.id ? { ...i, quantity: n } : i)).filter((i) => Number(i.quantity) > 0);
		const token = $UserStore.data;
		let message = keepError ? error : '';
		if (n <= 0) {
			await fetchData({ action: 'agents_remove_cart_item', token, item_id: item.id });
		} else {
			const res = await fetchData({
				action: 'agents_cart_add', token, cart_id: cart.id,
				sku: item.sku, quantity: n, store: item.store, name: item.name,
				price: item.price, price_before_discount: item.price_before_discount,
				product_id: item.product_id, parent_id: item.parent_id, parent_sku: item.parent_sku
			});
			// the server refuses more than the stock (it may have changed since the cart was opened)
			if (res && Number(res.success) === 0 && res.msg) message = `${item.size}: ${res.msg}`;
		}
		await load(cart.id, true);
		// load() clears messages; show this one after the cart is refreshed
		error = message;
	}

	async function removeGroup(g) {
		if (!confirm(`להסיר את ${g.name} (${g.color}) מהסל?`)) return;
		busy = true;
		for (const i of g.rows) await fetchData({ action: 'agents_remove_cart_item', token: $UserStore.data, item_id: i.id });
		await load(cart.id, true);
		busy = false;
	}

	async function chooseCustomer(c) {
		picking = false;
		busy = true;
		await fetchData({ action: 'agents_add_customer_to_cart', token: $UserStore.data, cart_id: cart.id, customer: c });
		await load(cart.id, true);
		refreshCartsList();
		busy = false;
	}

	async function send() {
		if (!cart.customer_id) {
			picking = true;
			return;
		}
		if (!confirm(`לשלוח הזמנה ל${cart.customer_name} על סך ${money(totals.total)} כולל מע״מ?`)) return;
		busy = true;
		error = '';
		const res = await fetchData({
			action: 'agents_finish_cart', token: $UserStore.data,
			cart_id: cart.id, order_user_name: ($UserStore.agent_name || '').trim()
		});
		if (res && Number(res.success) === 1) {
			clearActiveCart();
			await load(cart.id, true);
			sentNumber = (cart && cart.order_number) || '✓';
			refreshCartsList();
		} else {
			error = (res && res.msg) || 'שליחת ההזמנה נכשלה. נסה שוב, ואם זה חוזר פנה למשרד.';
		}
		busy = false;
	}

	async function deleteCart() {
		if (!confirm('למחוק את הסל? הפריטים שבו יימחקו.')) return;
		busy = true;
		await fetchData({ action: 'agents_delete_cart', token: $UserStore.data, cart_id: cart.id });
		if (String($activeCartId) === String(cart.id)) clearActiveCart();
		await refreshCartsList();
		busy = false;
		push('/agents/carts');
	}

	function newCart() {
		clearActiveCart();
		push('/agents/carts?new=1');
	}
</script>

<AppShell title={readOnly ? `הזמנה ${cart.order_number}` : 'סל'}>
	{#if loading}
		<div class="spinner" />
	{:else if !cart}
		<div class="empty-state">
			<b>הסל לא נמצא</b>ייתכן שהוא נמחק.
			<div class="mt"><button class="btn-primary" on:click={() => push('/agents/carts')}>לסלים הפתוחים</button></div>
		</div>
	{:else}
		{#if sentNumber}
			<div class="sent">
				<div class="check"><Icon name="check" size={34} /></div>
				<div>
					<b>ההזמנה נשלחה{sentNumber !== '✓' ? ` · מספר ${sentNumber}` : ''}</b>
					<span>היא נכנסה להזמנות הפתוחות בווריפון ותגיע למחסן.</span>
				</div>
				<div class="sent-actions">
					<button class="btn-primary small" on:click={newCart}>סל חדש</button>
					<button class="btn-ghost small" on:click={() => push('/agents/orders')}>להזמנות</button>
				</div>
			</div>
		{/if}

		<div class="layout">
			<div>
				<div class="customer">
					<div class="avatar">{(cart.customer_name || '?').trim().charAt(0)}</div>
					<div class="who">
						{#if cart.customer_id}
							<b>{cart.customer_name}</b>
							<span class="muted">לקוח {cart.customer_id} · {readOnly ? 'נשלחה' : 'נפתח'} {formatDate(cart.date_time)}</span>
						{:else}
							<b>לא נבחר לקוח</b>
							<span class="muted">סל {cart.id} · נפתח {formatDate(cart.date_time)}</span>
						{/if}
					</div>
					{#if !readOnly}
						<button class={cart.customer_id ? 'btn-ghost small' : 'btn-primary small'} on:click={() => (picking = true)} disabled={busy}>
							{cart.customer_id ? 'החלפת לקוח' : 'בחירת לקוח'}
						</button>
					{/if}
				</div>

				{#if !groups.length}
					<div class="empty-state">
						<b>הסל ריק</b>מוסיפים מוצרים ממסך המוצרים.
						<div class="mt"><button class="btn-primary" on:click={() => push('/agents/products')}>למוצרים</button></div>
					</div>
				{/if}

				{#each sections as s (s.brand)}
					<section class="brand-section">
					<div class="brand-head">
						<b>{s.brand}</b>
						{#if s.rule}<span class="muted rule">{s.rule}</span>{/if}
						<span class="brand-sum">{s.units} יח׳ · {money(Math.round(s.total * 100) / 100)}</span>
					</div>
				{#each s.groups as g (g.key)}
					<div class="line">
						<div class="thumb">{#if g.image}<img src={g.image} alt="" />{/if}</div>
						<div class="mid">
							<h3>{g.name}{g.color ? ' · ' + g.color : ''}</h3>
							<div class="muted sub">
								{g.model} · {money(g.unitPrice)} ליח׳
							</div>
							{#each g.byStore as part (part.store)}
							{#if g.byStore.length > 1 || part.store !== MAIN_STORE}
								<div class="store-tag">מלאי {storeName(stores, part.store)}</div>
							{/if}
							<div class="sizes">
								{#each part.rows as i (i.id)}
									{@const stock = Number(i.stock) || 0}
									<span class="size-chip" class:over={!readOnly && Number(i.quantity) > stock}>
										<span class="lbl">{i.size || '—'}</span>
										{#if readOnly}
											<span class="n">× {i.quantity}</span>
										{:else}
											<button on:click={() => changeQty(i, -1)} aria-label="פחות"><Icon name="minus" size={16} /></button>
											<input class="n" type="number" inputmode="numeric" min="0" max={Math.max(stock, Number(i.quantity))}
												value={i.quantity} aria-label="כמות במידה {i.size}"
												on:focus={(e) => e.target.select()}
												on:keydown={(e) => e.key === 'Enter' && e.target.blur()}
												on:change={(e) => typedQty(i, e.target)} />
											<button on:click={() => changeQty(i, 1)} disabled={Number(i.quantity) >= stock} aria-label="עוד"><Icon name="plus" size={16} /></button>
										{/if}
									</span>
								{/each}
							</div>
							{#if !readOnly}
								{@const short = part.rows.filter((i) => Number(i.quantity) > (Number(i.stock) || 0))}
								{#if short.length}
									<div class="short">
										המלאי ירד מאז שנוסף לסל: {short.map((i) => `${i.size} (במלאי ${Number(i.stock) || 0})`).join(', ')}. כדאי להוריד כמות.
									</div>
								{/if}
							{/if}
							{/each}
						</div>
						<div class="total">
							<b>{money(Math.round(g.total * 100) / 100)}</b>
							<span class="muted">{g.units} יח׳</span>
							{#if !readOnly}<button class="icon-btn" on:click={() => removeGroup(g)} aria-label="הסרה מהסל"><Icon name="trash" size={20} /></button>{/if}
						</div>
					</div>
				{/each}
					</section>
				{/each}

				{#if !readOnly && groups.length}
					<div class="add-more"><button class="btn-ghost" on:click={() => push('/agents/products')}><Icon name="plus" size={20} /> הוספת מוצרים</button></div>
				{/if}
			</div>

			<aside class="summary">
				<h3>{readOnly ? 'סיכום ההזמנה' : 'סיכום'}</h3>
				<div class="row"><span>{totals.units} יחידות · {models === 1 ? 'דגם אחד' : models + ' דגמים'}</span></div>
				{#if sections.length > 1}
					{#each sections as s (s.brand)}
						<div class="row brand-row"><span>{s.brand} · {s.units} יח׳</span><span>{money(Math.round(s.total * 100) / 100)}</span></div>
					{/each}
				{/if}
				<div class="row"><span>לפני מע״מ</span><span>{money(totals.beforeVat)}</span></div>
				<div class="row"><span>מע״מ {VAT_PERCENT}%</span><span>{money(totals.vat)}</span></div>
				<div class="row grand"><span>סה״כ</span><span>{money(totals.total)}</span></div>
				{#if error}<div class="alert-error">{error}</div>{/if}
				{#if !readOnly}
					<button class="btn-primary send" on:click={send} disabled={busy || !groups.length}>
						{busy ? 'שולח…' : cart.customer_id ? 'שליחת הזמנה' : 'בחירת לקוח ושליחה'}
					</button>
					<p class="note">ההזמנה תיכנס להזמנות הפתוחות בווריפון.</p>
					<div class="center"><button class="btn-danger-text" on:click={deleteCart} disabled={busy}>מחיקת הסל</button></div>
				{/if}
			</aside>
		</div>

		{#if !readOnly && groups.length}
			<div class="pay-bar">
				<div class="sum">{totals.units} יח׳ · כולל מע״מ<b>{money(totals.total)}</b></div>
				<button class="btn-primary" on:click={send} disabled={busy}>{busy ? 'שולח…' : 'שליחת הזמנה'}</button>
			</div>
		{/if}
	{/if}
</AppShell>

{#if picking}
	<CustomerPicker on:close={() => (picking = false)} on:select={(e) => chooseCustomer(e.detail)} />
{/if}

<style>
	.mt { margin-top: 16px; }
	.center { text-align: center; }
	.layout { display: grid; grid-template-columns: 1fr 360px; gap: 22px; align-items: start; }

	.sent {
		display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
		background: var(--ok-bg); color: var(--ok); border-radius: var(--radius); padding: 18px 20px; margin-bottom: 16px;
	}
	.sent .check { width: 56px; height: 56px; border-radius: 50%; background: var(--ok); color: #fff; display: grid; place-items: center; flex: none; }
	.sent b { display: block; font-size: 20px; color: var(--ink); }
	.sent > div:nth-child(2) { flex: 1; }
	.sent-actions { display: flex; gap: 8px; }

	.customer {
		display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 18px 20px;
		background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); margin-bottom: 16px;
	}
	.avatar { width: 52px; height: 52px; border-radius: 50%; background: var(--bg); display: grid; place-items: center; font-weight: 800; font-size: 20px; flex: none; }
	.who { flex: 1; min-width: 180px; display: flex; flex-direction: column; }
	.who b { font-size: 21px; }
	.who span { font-size: 15px; }

	.brand-section { margin-bottom: 18px; }
	.brand-head {
		display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap;
		padding: 4px 4px 10px; border-bottom: 2px solid var(--ink); margin-bottom: 12px;
	}
	.brand-head b { font-size: 20px; letter-spacing: .04em; }
	.brand-head .rule { font-size: 14px; }
	.brand-head .brand-sum { margin-inline-start: auto; font-weight: 700; }
	.summary .brand-row { font-size: 15px; padding: 4px 0; color: var(--muted); }
	.line {
		display: grid; grid-template-columns: 96px 1fr auto; gap: 16px; align-items: start;
		padding: 16px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); margin-bottom: 12px;
	}
	.thumb { width: 96px; aspect-ratio: 3 / 4; border-radius: 10px; overflow: hidden; background: var(--photo); }
	.thumb img { width: 100%; height: 100%; object-fit: cover; }
	h3 { margin: 0 0 2px; font-size: 18px; }
	.sub { font-size: 14px; margin-bottom: 10px; }
	.strike { text-decoration: line-through; margin-inline-start: 6px; }
	.sizes { display: flex; flex-wrap: wrap; gap: 6px; }
	/* compact so a full size run fits on one row */
	.size-chip {
		display: inline-flex; align-items: center; height: 40px;
		border: 1px solid var(--line-strong); border-radius: 10px; overflow: hidden; background: var(--surface);
	}
	.size-chip .lbl {
		padding: 0 8px; font-weight: 700; font-size: 15px; color: var(--ink-2); border-inline-end: 1px solid var(--line);
		height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; background: var(--bg); line-height: 1.1;
	}
	.store-tag { margin: 10px 0 6px; font-size: 14px; font-weight: 700; color: var(--warn); }
	.store-tag:first-of-type { margin-top: 0; }
	.sizes + .store-tag { margin-top: 12px; }
	.size-chip button { width: 30px; height: 100%; border: 0; background: none; color: var(--ink-2); display: grid; place-items: center; padding: 0; }
	.size-chip button:active { background: var(--bg); }
	.size-chip button:disabled { opacity: .25; }
	.size-chip.over { border-color: var(--danger); box-shadow: inset 0 0 0 1px var(--danger); }
	.short { margin-top: 8px; color: var(--danger); font-size: 14px; font-weight: 500; }
	.size-chip .n { min-width: 26px; padding: 0 6px; text-align: center; font-weight: 800; font-size: 16px; }
	.size-chip input.n {
		width: 36px; height: 100%; border: 0; border-inline: 1px solid var(--line); background: var(--surface);
		font: inherit; font-weight: 800; font-size: 16px; text-align: center; padding: 0;
	}
	.size-chip input.n:focus { outline: none; background: #FAFAF8; box-shadow: inset 0 0 0 2px var(--ink); }
	.total { text-align: end; display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
	.total b { font-size: 20px; }
	.total span { font-size: 14px; }
	.total .icon-btn { margin-top: 6px; color: var(--muted); }
	.add-more { display: flex; justify-content: center; margin: 6px 0 8px; }

	.summary { position: sticky; top: calc(var(--topbar-h) + 20px); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); padding: 20px; }
	.summary h3 { margin: 0 0 12px; }
	.summary .row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 17px; color: var(--ink-2); }
	.summary .grand { border-top: 1px solid var(--line); margin-top: 6px; padding-top: 14px; color: var(--ink); font-size: 22px; font-weight: 800; }
	.summary .alert-error { margin-top: 12px; }
	.send { width: 100%; margin-top: 16px; height: 60px; font-size: 19px; }
	.note { color: var(--muted); font-size: 14px; margin: 10px 0 0; line-height: 1.5; }

	.pay-bar {
		display: none; position: fixed; z-index: 19; inset-inline: 0; bottom: calc(var(--tabbar-h) + env(safe-area-inset-bottom));
		align-items: center; justify-content: space-between; gap: 12px;
		padding: 12px 24px; background: var(--surface); border-top: 1px solid var(--line); box-shadow: 0 -6px 20px rgba(0, 0, 0, .06);
	}
	.pay-bar .sum { font-size: 15px; color: var(--muted); }
	.pay-bar .sum b { display: block; font-size: 22px; color: var(--ink); }

	@media (max-width: 1000px) {
		.layout { grid-template-columns: 1fr; }
		.summary { position: static; }
		.pay-bar { display: flex; }
		.layout { padding-bottom: 90px; }
	}
	@media (max-width: 640px) {
		.line { grid-template-columns: 72px 1fr; }
		.thumb { width: 72px; }
		.total { grid-column: 1 / -1; flex-direction: row; justify-content: space-between; align-items: center; }
	}
</style>
